import { css, Interpolation, Theme } from '@emotion/react'
import {
    cloneElement,
    memo,
    ReactElement,
    RefObject,
    useEffect,
    useRef,
    useState,
} from 'react'
import { VList, VListHandle } from 'virtua'

const rootStyles = css({
    alignItems: 'center',
    display: 'flex',
    flexFlow: 'column',
    height: '100%',
    justifyContent: 'start',
    width: '100%',
    maxWidth: '100%',
    padding: 0,
})

interface IGridVirtual {
    children: ReactElement
    styleOverrides?: Interpolation<Theme>
    columns?: number
    columnWidth?: number
    list: Array<any>
    overscanRows?: number
    rowHeight?: number
    testId?: string
    onScroll?(offset: number): void
    onUpdateList?(listRef: RefObject<VListHandle>): void
}

const GridVirtual = memo((props: IGridVirtual) => {
    const {
        children,
        columns,
        columnWidth,
        styleOverrides,
        list,
        overscanRows,
        rowHeight,
        testId,
        onScroll = () => {},
        onUpdateList = () => {},
    } = props

    const [rowCount, setRowCount] = useState(0)
    const [gridSize, setGridSize] = useState({
        columnCount: 1,
        columnWidth: 1,
        height: 1,
        rowHeight: 1,
        width: 1,
    })

    const parentRef = useRef<HTMLDivElement>(null)
    const gridOuterRef = useRef<VListHandle>(null)
    const overscanRowCount = overscanRows || 4

    const onParentChanged = (parentCurrent: HTMLDivElement) => {
        const width = parentCurrent.offsetWidth
        const newColumnWidth = columns
            ? Math.floor(width / columns)
            : columnWidth
        const columnCount = columns || Math.floor(width / newColumnWidth)

        setGridSize({
            columnCount,
            columnWidth: newColumnWidth,
            height: parentCurrent.offsetHeight,
            rowHeight: rowHeight || newColumnWidth,
            width,
        })
    }

    useEffect(() => {
        setRowCount(Math.ceil(list.length / gridSize.columnCount))
        onUpdateList(gridOuterRef)
    }, [list.length, gridSize.columnCount])

    useEffect(() => {
        onParentChanged(parentRef.current)
    }, [parentRef, gridOuterRef])

    const rowRenderer = (index: number) => {
        const startIndex = index * gridSize.columnCount
        return (
            <div
                key={index}
                style={{ display: 'flex', height: gridSize.rowHeight }}
            >
                {Array.from({ length: gridSize.columnCount }).map(
                    (_, colIndex) => {
                        const globalIndex = startIndex + colIndex
                        return globalIndex < list.length ? (
                            <div
                                key={colIndex}
                                style={{
                                    width: gridSize.columnWidth,
                                    height: gridSize.rowHeight,
                                }}
                            >
                                {cloneElement(children, {
                                    columnIndex: colIndex,
                                    rowIndex: index,
                                    itemData: list[globalIndex],
                                    index: globalIndex,
                                } as object)}
                            </div>
                        ) : null
                    },
                )}
            </div>
        )
    }

    return (
        <div
            css={[rootStyles, styleOverrides]}
            ref={parentRef}
            data-testid={testId || 'Virtual__Grid'}
        >
            <VList
                ref={gridOuterRef}
                count={rowCount}
                onScroll={onScroll}
                overscan={overscanRowCount}
                className="no-scrollbars"
                style={{
                    height: gridSize.height,
                    width: gridSize.width,
                }}
            >
                {rowRenderer}
            </VList>
        </div>
    )
})

export default GridVirtual
